import { type MutableRefObject, useCallback, useEffect, useMemo } from 'react'
import type { PhotoActionHandlers } from '@/components/app/photos/photo-actions-list'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { useLiveSelectedShapes } from '@/hooks/use-live-selected-shapes'
import { usePhotoPanelContext } from '@/hooks/use-photo-panel-context'
import type { PhotoActionId } from '@/lib/photos/photo-action-items'
import type { CenterCanvasConfig } from '../types/center-canvas-config'

/**
 * The floating photo action bar and Photo Effects popover: which single selected photo they float
 * over (none while another single-shape mode or a text edit owns the canvas), the bar's handlers
 * and enablement, closing Photo Effects when that photo goes away, and applying a PEZ preview.
 */
export function usePhotoActionBar({
  internalShapeManagerRef,
  activeItem,
  isCropMode,
  isNodeEditMode,
  isTextOnPathMode,
  isShapeTextEditing,
  selectedShapeIdsForUnified,
  tryEnterCropMode,
  isRemovingBackgroundShapeId,
  photoEffectsOpen,
  onPhotoEffectsOpenChange,
  onEditTextPath,
  onRemoveBackgroundFromShape,
  onAddCaption,
  onRemovePhotoFromShape,
  onShapeCommitted,
}: {
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  activeItem: string
  isCropMode: boolean
  isNodeEditMode: boolean
  isTextOnPathMode: boolean
  isShapeTextEditing: boolean
  selectedShapeIdsForUnified: string[]
  tryEnterCropMode: (shape: ShapeData) => boolean
  isRemovingBackgroundShapeId: CenterCanvasConfig['isRemovingBackgroundShapeId']
  photoEffectsOpen: boolean
  onPhotoEffectsOpenChange: CenterCanvasConfig['onPhotoEffectsOpenChange']
  onEditTextPath: CenterCanvasConfig['onEditTextPath']
  onRemoveBackgroundFromShape: CenterCanvasConfig['onRemoveBackgroundFromShape']
  onAddCaption: CenterCanvasConfig['onAddCaption']
  onRemovePhotoFromShape: CenterCanvasConfig['onRemovePhotoFromShape']
  onShapeCommitted: CenterCanvasConfig['onShapeCommitted']
}) {
  const liveSelectedShapes = useLiveSelectedShapes(internalShapeManagerRef)

  const selectedPhotoShapeForToolbar = useMemo(() => {
    // `isTextOnPathMode` belongs in this list for the same reason the other three do: it is a
    // single-shape canvas mode with its own floating bar, and unlike crop mode it deliberately
    // KEEPS its target selected. Without it, putting a photo-filled shape on a path floats the
    // photo action bar on top of the Text on a Path bar.
    if (
      activeItem !== 'select' ||
      isCropMode ||
      isNodeEditMode ||
      isTextOnPathMode ||
      isShapeTextEditing
    ) {
      return undefined
    }

    if (selectedShapeIdsForUnified.length !== 1) return undefined

    const shape = liveSelectedShapes.find((s) => s.id === selectedShapeIdsForUnified[0])

    if (!shape?.photoFill) return undefined

    return shape
  }, [
    activeItem,
    isCropMode,
    isNodeEditMode,
    isTextOnPathMode,
    isShapeTextEditing,
    selectedShapeIdsForUnified,
    liveSelectedShapes,
  ])

  const photoToolbarRemovingBackground = Boolean(
    selectedPhotoShapeForToolbar && isRemovingBackgroundShapeId?.(selectedPhotoShapeForToolbar.id)
  )

  const { isActionDisabled: isPhotoToolbarActionDisabled } = usePhotoPanelContext(
    selectedPhotoShapeForToolbar,
    {
      interactionLocked: selectedPhotoShapeForToolbar?.locked ?? false,
      isRemovingBackground: photoToolbarRemovingBackground,
    }
  )

  const photoToolbarHandlers = useMemo<PhotoActionHandlers>(() => {
    if (!selectedPhotoShapeForToolbar) return {}

    const shapeId = selectedPhotoShapeForToolbar.id

    return {
      photoEffects: onPhotoEffectsOpenChange
        ? () => onPhotoEffectsOpenChange(!photoEffectsOpen)
        : undefined,
      cropPhoto: () => {
        tryEnterCropMode(selectedPhotoShapeForToolbar)
      },
      clippingPath: onEditTextPath ? () => onEditTextPath(shapeId) : undefined,
      removeBackground: onRemoveBackgroundFromShape
        ? () => onRemoveBackgroundFromShape(shapeId)
        : undefined,
      caption: onAddCaption,
      removePhoto: onRemovePhotoFromShape ? () => onRemovePhotoFromShape(shapeId) : undefined,
    }
  }, [
    selectedPhotoShapeForToolbar,
    tryEnterCropMode,
    onEditTextPath,
    onRemoveBackgroundFromShape,
    onAddCaption,
    onRemovePhotoFromShape,
    onPhotoEffectsOpenChange,
    photoEffectsOpen,
  ])

  const isPhotoToolbarActionActive = useCallback(
    (id: PhotoActionId) => id === 'photoEffects' && photoEffectsOpen,
    [photoEffectsOpen]
  )

  // Close Photo Effects when the selection is no longer a single photo (WMPCM-9470).
  useEffect(() => {
    if (!photoEffectsOpen) return
    if (selectedPhotoShapeForToolbar) return

    onPhotoEffectsOpenChange?.(false)
  }, [photoEffectsOpen, selectedPhotoShapeForToolbar, onPhotoEffectsOpenChange])

  /** WMPCM-596 Step 10 — PEZ preview URL + pezId on the placed photo fill. */
  const handlePhotoPezApplied = useCallback(
    ({
      shapeId,
      pezId,
      pezUrl,
      historyTxnId,
    }: {
      shapeId: string
      pezId: string
      pezUrl: string
      historyTxnId?: string
    }) => {
      const mgr = internalShapeManagerRef.current

      if (!mgr) return

      const existing = mgr.getShapes().find((s) => s.id === shapeId)

      if (!existing?.photoFill) return

      const photoFill = { ...existing.photoFill, pezId, previewUrl: pezUrl }

      mgr.updateShape(shapeId, { photoFill })

      const updated = { ...existing, photoFill }

      onShapeCommitted?.(updated, 'update', {
        before: { photoFill: existing.photoFill },
        after: { photoFill: updated.photoFill },
        ...(historyTxnId ? { historyTxnId } : {}),
      })
    },
    [onShapeCommitted, internalShapeManagerRef]
  )

  return {
    selectedPhotoShapeForToolbar,
    photoToolbarRemovingBackground,
    isPhotoToolbarActionDisabled,
    photoToolbarHandlers,
    isPhotoToolbarActionActive,
    handlePhotoPezApplied,
  }
}
